import React, {useEffect, useState} from 'react';
import {Alert, Button, Card, Col, List, Row, Spin, Tag, Typography} from 'antd';
import {useNavigate, useParams} from 'react-router-dom';
import api from '../../api';
import {useTranslations} from '../../lang';
import SEOHead from '../../Components/SEOHead';
import CdnImage from '../../Components/CdnImage';
import CropInfo from '../../Components/CropInfo';
import {safeExternalUrl, serializeJsonLd} from '../../htmlSafety';

const {Title, Paragraph} = Typography;

export default function ProductDetail() {
    const {id} = useParams();
    const navigate = useNavigate();
    const {translate} = useTranslations();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setLoading(true);
        setNotFound(false);
        api.getProduct(id)
            .then(data => {
                if (data) setProduct(data);
                else setNotFound(true);
            })
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) return <Spin spinning><div style={{minHeight: 240}}/></Spin>;
    if (notFound || !product) {
        return <Alert type="warning" message={translate('Product niet gevonden')} showIcon/>;
    }

    const name = [product.name, product.variety].filter(Boolean).join(' ');
    const description = product.description || `${name} in de gewassendatabase van Oogst.app`;
    const image = safeExternalUrl(product.image);
    const activeOffers = (product.offers || [])
        .filter(offer => offer.is_active !== false)
        .map(offer => ({...offer, safeUrl: safeExternalUrl(offer.url || offer.supplier?.website)}))
        .filter(offer => offer.safeUrl);
    const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name,
        description,
        image: image || undefined,
        url: `${window.location.origin}/products/${product.id}`,
        offers: activeOffers.map(offer => ({
            '@type': 'Offer',
            url: offer.safeUrl,
            availability: 'https://schema.org/InStock',
            seller: offer.supplier?.name ? {'@type': 'Organization', name: offer.supplier.name} : undefined,
        })),
    };

    return (
        <div style={{maxWidth: 1000, margin: '0 auto'}}>
            <SEOHead title={`${name} | Oogst.app`} description={description} ogImage={image} ogImageAlt={name}/>
            <script type="application/ld+json">{serializeJsonLd(structuredData)}</script>
            <Button onClick={() => navigate('/products')} style={{marginBottom: 16}}>
                {translate('Terug naar de gewassendatabase')}
            </Button>
            <Card>
                <Row gutter={[24, 24]}>
                    {image && <Col xs={24} md={8}><CdnImage src={image} alt={name} width="100%"/></Col>}
                    <Col xs={24} md={image ? 16 : 24}>
                        <Title level={1}>{name}</Title>
                        <CropInfo crop={product} disableEdit/>
                        {product.description && <Paragraph>{product.description}</Paragraph>}
                    </Col>
                </Row>
            </Card>
            {activeOffers.length > 0 && (
                <Card title={translate('Verkrijgbaar bij')} style={{marginTop: 16}}>
                    <List dataSource={activeOffers} renderItem={offer => (
                        <List.Item actions={[<Button key="visit" type="primary" href={offer.safeUrl} target="_blank" rel="noopener noreferrer">{translate('Bekijk product')}</Button>]}>
                            <List.Item.Meta title={offer.supplier?.name || translate('Leverancier')} description={offer.is_active !== false && <Tag color="green">{translate('Beschikbaar')}</Tag>}/>
                        </List.Item>
                    )}/>
                </Card>
            )}
        </div>
    );
}
